import { PLAYER_PORTRAIT_PATH, UI_CHROME_ART } from '../../content/assets/uiArt';
import type { UiSymbolName } from '../../content/assets/uiSymbols';
import { protagonist } from '../../content/protagonist/protagonist';
import type { StatKey } from '../../domain/progress/progression';
import { useGameStore } from '../../store/gameStore';
import { selectPlayer } from '../../store/selectors';
import { SvgIcon } from '../chrome/SvgIcon';
import { InventoryPanel } from './InventoryPanel';

const STAT_ROWS: { key: StatKey; icon: UiSymbolName; label: string; tone: string }[] = [
  { key: 'strength', icon: 'stat-strength', label: 'Sức mạnh', tone: 'red' },
  { key: 'intelligence', icon: 'stat-intelligence', label: 'Trí tuệ', tone: 'blue' },
  { key: 'dexterity', icon: 'stat-dexterity', label: 'Nhanh nhẹn', tone: 'green' },
  { key: 'luck', icon: 'stat-luck', label: 'May mắn', tone: 'purple' },
];

export function CharacterPanel() {
  const player = useGameStore(selectPlayer);
  const allocateStatPoint = useGameStore((state) => state.allocateStatPoint);
  const manaPercent = player.maxMana > 0 ? `${(player.mana / player.maxMana) * 100}%` : '0%';
  const hpPercent = player.alive ? '100%' : '0%';
  const expPercent = player.expToNext > 0
    ? `${Math.min(100, (player.exp / player.expToNext) * 100)}%`
    : '0%';
  const itemCount = player.inventory.reduce((sum, stack) => sum + stack.quantity, 0);

  return (
    <aside className="character-panel">
      <header className="character-identity">
        <div className="level-crest" aria-label={`Cấp ${player.level}`}>
          <img src={UI_CHROME_ART.levelCrest} alt="" aria-hidden="true" />
          <small>LV.</small>
          <strong>{player.level}</strong>
        </div>
        <div className="character-copy">
          <small>{protagonist.title.toUpperCase()}</small>
          <strong>{protagonist.name}</strong>
          <span>Phe {player.faction}</span>
        </div>
      </header>

      <img className="character-watermark" src={PLAYER_PORTRAIT_PATH} alt="" aria-hidden="true" />

      <div className="resource-stack">
        <ResourceBar
          icon="resource-health"
          label="Sinh mệnh"
          value={`${player.hp}/${player.maxHp}`}
          percent={hpPercent}
          tone="health"
        />
        <ResourceBar
          icon="resource-mana"
          label="Ma lực"
          value={`${player.mana}/${player.maxMana}`}
          percent={manaPercent}
          tone="mana"
        />
        <ResourceBar
          icon="resource-exp"
          label="Kinh nghiệm"
          value={`${player.exp}/${player.expToNext}`}
          percent={expPercent}
          tone="exp"
        />
      </div>

      <p className="one-hit-note">Một sinh mệnh · Luôn hành động trước</p>

      <details className="hud-drawer stats-drawer">
        <summary>
          <span>Thuộc tính</span>
          {player.statPoints > 0 ? <b>{player.statPoints} điểm</b> : <small>Xem</small>}
        </summary>
        <div className="stats-list">
          {STAT_ROWS.map((row) => (
            <div className="stat-row" key={row.key}>
              <SvgIcon className={`stat-icon ${row.tone}`} name={row.icon} />
              <small>{row.label}</small>
              <strong>{player.stats[row.key]}</strong>
              <button
                type="button"
                className="stat-plus"
                disabled={player.statPoints <= 0}
                aria-label={`Cộng ${row.label}`}
                onClick={() => allocateStatPoint(row.key)}
              >
                +
              </button>
            </div>
          ))}
        </div>
      </details>

      <details className="hud-drawer inventory-drawer">
        <summary>
          <span>Hành trang</span>
          <small>{itemCount} vật phẩm</small>
        </summary>
        <InventoryPanel compact />
      </details>

      <footer className="character-footer">
        <span><SvgIcon name="currency-gold" /> {player.gold.toLocaleString('vi-VN')}</span>
        <span><SvgIcon name="action-skills" /> {player.skillIds.length} kỹ năng</span>
      </footer>
    </aside>
  );
}

interface ResourceBarProps {
  icon: UiSymbolName;
  label: string;
  value: string;
  percent: string;
  tone: 'health' | 'mana' | 'exp';
}

function ResourceBar({ icon, label, value, percent, tone }: ResourceBarProps) {
  return (
    <div className={`resource-block resource-${tone}`}>
      <div className="resource-label">
        <span><SvgIcon name={icon} />{label}</span>
        <strong>{value}</strong>
      </div>
      <div className={`resource-track ${tone}`} aria-hidden="true">
        <i style={{ width: percent }} />
      </div>
    </div>
  );
}
